import React, { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import TableTab from "../../../components/tabs/table-tab";
import { REPORTS_FILTER_DATE_TIME_CURRENT_WEEK } from "../../../constants/element-type";
import { FILTER_COMPANIES, FILTER_DATE, FILTER_DEPARTMENTS, FILTER_PROFILE, FILTER_SITES, FILTER_TAGHOLDERS } from "../../../constants/filter-types";
import { REPORTS_ABSENTEEISM } from "../../../constants/menu-types";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import AbsenteeismCurrentWeekReportColumns from "../../../models/ColumnDefinitions/AbsenteeismCurrentWeekReportColumns";
import { Filter } from "../../../models/Filter";
import { ListQuery } from "../../../models/ListQuery";
import { Lookup } from "../../../models/Lookup";
import LookupTypes from "../../../models/LookupTypes";
import AbsenteeismCurrentWeekReportSearch from "../../../models/search/AbsenteeismCurrentWeekReportSearch";
import CategorySearch from "../../../models/search/CategorySearch";
import CompanySearch from "../../../models/search/CompanySearch";
import DepartmentSearch from "../../../models/search/DepartmentSearch";
import SiteSearch from "../../../models/search/SiteSearch";
import { auditRunReport } from "../../../services/auditService";
import { getCategoryNameWithPagination } from "../../../services/categoryService";
import { getCompanyNamesWithPagination } from "../../../services/companyService";
import { getDepartmentNamesWithPagination } from "../../../services/departmentService";
import { getAbsenteeismReport } from "../../../services/reportService";
import { getSiteNamesWithPagination } from "../../../services/siteService";
import { getEnrolmentNamesWithPagination } from "../../../services/enrolmentService";
import { getUserFiltersByView } from "../../../services/userPreferenceService";
import { getErrorMessage, showError } from "../../../services/utilities/ServiceError";
import { getCurrentWeekDates } from "../../../utilities/DateUtils";
import EnrolmentSearch from "@/models/search/EnrolmentSearch";

interface AbseenteeismCurrentWeekReportProps {
    reportId: string;
}

const AbseenteeismCurrentWeekReport: FC<AbseenteeismCurrentWeekReportProps> = ({ reportId }) => {
    const { translate } = useLocale();
    const userLoginId = useSelector((state: any) => (state.userInfo.id));
    const currentMenuDetail = REPORTS_ABSENTEEISM;

    const lookup: Lookup[] = useMemo(() => [
        {
            name: LookupTypes.company,
            values: [],
            dataMethod: getCompanyNamesWithPagination,
            searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.department,
            values: [],
            dataMethod: getDepartmentNamesWithPagination,
            searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.profileId,
            values: [],
            dataMethod: getCategoryNameWithPagination,
            searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: "companyId",
            values: [],
            dataMethod: getCompanyNamesWithPagination,
            searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: "departmentId",
            values: [],
            dataMethod: getDepartmentNamesWithPagination,
            searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: "profileId",
            values: [],
            dataMethod: getCategoryNameWithPagination,
            searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: "masterId",
            values: [],
            dataMethod: getEnrolmentNamesWithPagination,
            searchAccessor: EnrolmentSearch.find(column => column.accessor === "name")?.searchOnName
        },
    ], []);

    const getOriginalStaticFilter = async (filterKey: string) => {
        const date = new Date();
        switch (filterKey) {
            case "dateOnly":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    clearPolicy: "locked",
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.date),
                    options: [],
                    selected: [{
                        elementSysname: REPORTS_FILTER_DATE_TIME_CURRENT_WEEK,
                        label: translate.byKey(TranslationKeys.currentWeek),
                        value: getCurrentWeekDates(date),
                    }],
                };
            case "siteId":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.site),
                    options: [],
                    selected: [{ ignore: true, label: translate.byKey(TranslationKeys.all), value: '*' }],
                };
            case "masterId":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.displayName),
                    options: [],
                    selected: [{ ignore: true, label: translate.byKey(TranslationKeys.all), value: '*' }],
                };
            case "companyId":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.company),
                    options: [],
                    selected: [{ ignore: true, label: translate.byKey(TranslationKeys.all), value: '*' }],
                };
            case "departmentId":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.department),
                    options: [],
                    selected: [{ ignore: true, label: translate.byKey(TranslationKeys.all), value: '*' }],
                };
            case "profileId":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.category),
                    options: [],
                    selected: [{ ignore: true, label: translate.byKey(TranslationKeys.all), value: '*' }],
                };
            default:
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: filterKey,
                    options: [],
                    selected: [{ ignore: true, label: translate.byKey(TranslationKeys.all), value: '*' }],
                };
        }
    };

    const initialiseUserPreferences = async (setDetails: (userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys) => void) => {
        const staticFilterKeys = ["dateOnly", "siteId", "companyId", "departmentId", "profileId", "masterId"];
        let customFilter: Filter[] = [];

        try {
            const savedFilters = await getUserFiltersByView(userLoginId, currentMenuDetail, reportId);

            if (!savedFilters.find(f => f.filterId === FILTER_DATE)) {
                customFilter.push(await getOriginalStaticFilter("dateOnly"));
            }
            if (!savedFilters.find(f => f.filterId === FILTER_SITES)) {
                customFilter.push(await getOriginalStaticFilter("siteId"));
            }
            if (!savedFilters.find(f => f.filterId === FILTER_COMPANIES)) {
                customFilter.push(await getOriginalStaticFilter("companyId"));
            }
            if (!savedFilters.find(f => f.filterId === FILTER_DEPARTMENTS)) {
                customFilter.push(await getOriginalStaticFilter("departmentId"));
            }
            if (!savedFilters.find(f => f.filterId === FILTER_PROFILE)) {
                customFilter.push(await getOriginalStaticFilter("profileId"));
            }
            if (!savedFilters.find(f => f.filterId === FILTER_TAGHOLDERS)) {
                customFilter.push(await getOriginalStaticFilter("masterId"));
            }
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setDetails(userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys);
        }
    };

    const getDataMethod = async (listParameters?: ListQuery) => {
        try {
            await auditRunReport(reportId);
            return getAbsenteeismReport((translate.byKey(TranslationKeys.absent)), translate, listParameters);
        } catch (error) {
            return Promise.reject(getErrorMessage(error));
        }
    }

    return <TableContextProvider
        dataMethod={getDataMethod}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        tableDescriptor={AbsenteeismCurrentWeekReportColumns}
        searchDescriptor={AbsenteeismCurrentWeekReportSearch}
        useFilter
        useSort
        usePagination
        useSearch
        useTable
        lookup={lookup}
        loadDataOnDemand
        runDataOnDemandButtonLabel={translate.byKey(TranslationKeys.runReport)}
        getOriginalStaticFilter={getOriginalStaticFilter}>
        <TableTab
            title={''}
            reportTitle={translate.byKey(TranslationKeys.absenteeismCurrentWeek)}
            selectRows={false}
            showFilterMenu={true}
            cyName="abseenteeismCurrentWeekReport"/>
    </TableContextProvider >
};

export default AbseenteeismCurrentWeekReport;

